import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useOutletContext } from 'react-router';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { UpdatedCrewMember } from '../../../types/crew';
import CrewForm from './CrewForm';
import { CrewTemplateSubPageContext } from './types';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const EditCrewMemberPage = () => {
  const {
    crewMember,
    isFetching,
    updateContextCrewMember,
    error,
    setDisplayedCrewMemberFullName,
  } = useOutletContext<CrewTemplateSubPageContext>();

  const { t } = useLanguage();
  const { updateCrewMember } = useIotsApi();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  const updateCrewMemberMutation = useMutation({
    mutationFn: updateCrewMember,
  });

  return (
    <CrewForm
      onCrewMemberNameChange={setDisplayedCrewMemberFullName}
      onSubmit={(values, callback) => {
        const updatedCrewMember: UpdatedCrewMember = {
          id: crewMember.id,
          firstName: values.firstName,
          lastName: values.lastName,
        };

        updateCrewMemberMutation.mutate(updatedCrewMember, {
          onSuccess: () => {
            enqueueSnackbar({
              variant: 'success',
              message: t('content.crew-update-success'),
            });
            // A successful update will just update the item cached data
            updateContextCrewMember({
              ...crewMember,
              ...updatedCrewMember,
            });
            callback();
          },
          onError: error => {
            enqueueSnackbar({
              variant: 'error',
              message: getErrorMessage(error, t('content.crew-update-error')),
            });
            updateCrewMemberMutation.reset();
          },
        });
      }}
      crewMember={crewMember}
      isLoading={isFetching || updateCrewMemberMutation.isPending}
      disabled={Boolean(error)}
    />
  );
};

export default EditCrewMemberPage;
